import { useState } from 'react';
import { useIntl } from 'react-intl';
import { SelectionableSection } from 'sonata-react-components';

import { DistributionSlider } from '@/components/common/DistributionSlider/DistributionSlider';
import { getUserMarketplaceDistribution } from '@/helpers/user/user.helpers';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { MarketplaceDistributionStyled } from './MarketplaceDistribution.styles';

import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';

type MarketplaceDistributionType = 'auto' | 'specific';

type Props = {
  budget: number;
  bidModels: PricingModelConfig['bidModels'];
  onChange: (premiumDistribution: number, openDistribution: number) => void;
};

/** Hybrid marketplace question: distribute automatically (both distributions 0) or set a specific Open/Premium split. */
export function MarketplaceDistribution({ budget, bidModels, onChange }: Props) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser).toJSON();
  const premiumDistribution = bidModels.find(bidModel => bidModel.mediaType === 'pmp')?.distribution ?? 0;
  const openDistribution = bidModels.find(bidModel => bidModel.mediaType === 'openRtb')?.distribution ?? 0;
  // ponytail: local state, not derived — a 0/0 user default would otherwise flip "specific" straight back to auto
  const [selectedType, setSelectedType] = useState<MarketplaceDistributionType>(() =>
    premiumDistribution === 0 && openDistribution === 0 ? 'auto' : 'specific'
  );

  const handleTypeChange = (type: MarketplaceDistributionType) => {
    setSelectedType(type);
    if (type === 'auto') {
      onChange(0, 0);
    } else {
      const { openRtb, pmp } = getUserMarketplaceDistribution(user);
      onChange(pmp, openRtb);
    }
  };

  return (
    <MarketplaceDistributionStyled className="marketplaceDistribution" id="marketplaceDistribution__box">
      <div className="marketplaceDistribution__title">
        {formatMessage({
          defaultMessage: 'Set marketplace distribution',
          id: 'campaignMarketplace.distribution.question'
        })}
      </div>
      <div className="marketplaceDistribution__body" key={selectedType}>
        <SelectionableSection
          id="marketplaceDistribution__auto"
          isSectionEnabled={selectedType === 'auto'}
          label={formatMessage({
            defaultMessage: 'Distribute automatically between Open and Premium marketplaces.',
            id: 'campaignMarketplace.distribution.auto'
          })}
          onSectionEnabledChange={() => handleTypeChange('auto')}
        />
        <SelectionableSection
          id="marketplaceDistribution__specific"
          isSectionEnabled={selectedType === 'specific'}
          label={formatMessage({
            defaultMessage: 'Set a specific distribution between Open and Premium marketplaces.',
            id: 'campaignMarketplace.distribution.specific'
          })}
          onSectionEnabledChange={() => handleTypeChange('specific')}
        />
        {selectedType === 'specific' ? (
          <DistributionSlider
            budget={budget}
            className="marketplaceDistribution__range"
            id="marketplaceDistribution__range"
            leftLabel={formatMessage({
              defaultMessage: 'Open',
              id: 'campaignMarketplace.distribution.left'
            })}
            onChange={value => onChange(value, 100 - value)}
            rightLabel={formatMessage({
              defaultMessage: 'Premium',
              id: 'campaignMarketplace.distribution.right'
            })}
            title={formatMessage({
              defaultMessage: 'Distribution percentage between Open and Premium marketplaces',
              id: 'campaignMarketplace.distribution.title'
            })}
            value={premiumDistribution}
          />
        ) : null}
      </div>
    </MarketplaceDistributionStyled>
  );
}
